import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Plus, Sparkles, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Platforms() {
  const { user } = useAuth();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/platforms`).then(res => { setPlatforms(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const iconMap = {
    'message-square': Sparkles,
    'image': Sparkles,
    'bot': Sparkles,
    'sparkles': Sparkles,
    'palette': Sparkles,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="platforms-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">AI Platforms</h1>
          <p className="text-muted-foreground">Browse prompts by AI platform</p>
        </div>
        {user && (
          <Link to="/submit-platform">
            <Button className="rounded-full gap-2 shadow-lg shadow-primary/20" data-testid="submit-platform-nav-btn">
              <Plus className="w-4 h-4" /> Submit Platform
            </Button>
          </Link>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-36 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="platforms-grid">
          {platforms.map(platform => (
            <Link key={platform.id} to={`/platform/${platform.slug}`} data-testid={`platform-card-${platform.slug}`}>
              <Card className="p-6 border-border/50 hover:-translate-y-1 hover:border-primary/50 transition-all duration-300 group">
                <div className="flex items-start gap-4">
                  {platform.favicon_url ? (
                    <img src={platform.favicon_url} alt={platform.name} className="w-12 h-12 rounded-xl object-contain bg-muted p-1.5 shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <Sparkles className="w-6 h-6" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors">{platform.name}</h3>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{platform.description}</p>
                    {platform.website && (
                      <span className="inline-flex items-center gap-1 text-xs text-primary">
                        <Globe className="w-3 h-3" /> {platform.website.replace(/https?:\/\//, '').split('/')[0]}
                      </span>
                    )}
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
